import * as yup from 'yup'
import {
  isValid,
  parse,
  format,
  format as dateFnsFormat,
} from 'date-fns'
import * as permissionType from 'constants/permissions'

export const formatTimestamp = (timestamp: string, format?: string) => {
  return dateFnsFormat(new Date(timestamp), format || 'dd.MM.yyyy HH:mm')
}

export const formatMoney = (value: number | string) => {
  return `${(Number(value) / 100).toFixed(2)} €`
}

export const hasPermission = (permissions: any, permission: string) => {
  const permissionItem = (permissions || []).filter(
    (item: { name: string; allowed: boolean }) =>
      item.name === permission && item.allowed,
  )

  return !!(permissionItem || [])
    .map((item: any) => item.allowed)
    .reduce((prev: any, next: any) => prev + next, 0)
}

export const myParkingLotPermission = (permissions): boolean => {
  return myParkingLotParkingLotPermission(permissions)
    || myParkingLotPaymentsPermission(permissions)
    || myParkingLotAnalyticsPermission(permissions)
    || myParkingLotCustomerNotificationExclusionsPermission(permissions)
}
export const myParkingLotParkingLotPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_MY_PARKING_LOT_PARKING_LOT)
}
export const parkingLotModifyPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_MY_PARKING_LOT_PARKING_LOT_MODIFY)
}
export const parkingLotNotificationsPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_MY_PARKING_LOT_PARKING_LOT_NOTIFICATIONS)
}
export const myParkingLotPaymentsPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_MY_PARKING_LOT_PAYMENTS)
}
export const myParkingLotAnalyticsPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_MY_PARKING_LOT_ANALYTICS)
}
export const myParkingLotCustomerNotificationExclusionsPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_MY_PARKING_LOT_CUSTOMER_NOTIFICATION_EXCLUSIONS)
}

export const subscriptionsPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_SUBSCRIPTIONS_BUY)
    || subscriptionsFreeTimePermission(permissions)
    || subscriptionsSubscriptionsPermission(permissions)
    || subscriptionsPermitsPermission(permissions)
    || subscriptionsSubscriptionOffersPermission(permissions)
}
export const subscriptionsFreeTimePermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_SUBSCRIPTIONS_FREE_TIME)
}
export const subscriptionsSubscriptionsPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_SUBSCRIPTIONS_SUBSCRIPTIONS)
}
export const subscriptionsPermitsPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_SUBSCRIPTIONS_PERMITS)
}
export const subscriptionsSubscriptionOffersPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_SUBSCRIPTIONS_SUBSCRIPTION_OFFERS)
}

export const reservationsPermission = (permissions): boolean => {
  return reservationsMyReservationsPermission(permissions)
    || reservationsReservationsPermission(permissions)
    || reservationsReservationOffersPermission(permissions)
}
export const reservationsMyReservationsPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_RESERVATIONS_MY_RESERVATIONS)
}
export const reservationsReservationsPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_RESERVATIONS_RESERVATIONS)
}
export const reservationsReservationOffersPermission = (permissions): boolean => {
  return hasPermission(permissions, permissionType.PAGE_RESERVATIONS_RESERVATION_OFFERS)
}

export function yupDateTimeField(options: { required?: boolean } = {}) {
  return options.required
    ? yup.string()
      .trim()
      .required()
      .test({
        test: value => {
          return isDateTimeFieldValid(value)
        },
      })
    : yup.string()
      .trim()
      .test({
        test: value => {
          return value == '' || isDateTimeFieldValid(value)
        },
      })
}

export function isDateTimeFieldValid(value: string) {
  return isValid(parseDateTimeField(value))
}

export function yupDateField(options: { required?: boolean } = {}) {
  return options.required
    ? yup.string()
      .trim()
      .required()
      .test({
        test: value => {
          return isDateFieldValid(value)
        },
      })
    : yup.string()
      .trim()
      .test({
        test: value => {
          return value == '' || isDateFieldValid(value)
        },
      })
}

export function isDateFieldValid(value: string) {
  return isValid(parseDateField(value))
}

export function formatToDateField(date: string | Date) {
  return format(new Date(date), 'dd.MM.yyyy')
}

export function parseDateField(value: string) {
  return parse(value, 'dd.MM.yyyy', new Date())
}
export function parseDateTimeField(value: string) {
  return parse(value, 'dd.MM.yyyy HH:mm', new Date())
}

export function formatDateFieldToISO(value: string) {
  return parseDateField(value).toISOString()
}
export function formatDateTimeFieldToISO(value: string) {
  return parseDateTimeField(value).toISOString()
}

export function formatDateFieldToDate(value: string) {
  return format(parseDateField(value), 'yyyy-MM-dd')
}

export function onlyNumeric(st: string): string {
  return st.replace(/[^0-9]/gi, '')
}

export const normalizePlate = (plate: string): string => {
  const from = 'АаВвЕеЁёИиЙйКкМмНнОоРрСсТтУуХхĀāČčĒēĢģĪīĶķĻļŅņŌōŖŗŠšŪūŽž' // Correct common Russian alphabet characters
  const to = 'AABBEEEENNNNKKMMHHOOPPCCTTYYXXAACCEEGGIIKKLLNNOORRSSUUZZ' // as well as possible Russian/Latvian mistypes
  return plate
    .replace(/[^0-9A-Za-z]/g, c => {
      const i = from.indexOf(c)
      return i >= 0 ? to[i] : ''
    })
    .toUpperCase()
}
